import React, { useState } from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails } from '../../core/_models'
import GeneralPricing from './components/GeneralPricing'
import SeasonalPricing from './components/SeasonalPricing'
import Discounts from './components/Discounts'
import Promotions from './components/Promotions'
import Extras from './components/Extras'
import { useQuery } from 'react-query'
import { getAll } from '../../../../../services/common_requests'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'

interface PricingTabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  selectedLanguage: string
}

// Sub-tab types
type PricingSubTab = 'general' | 'seasonal' | 'discounts' | 'promotions' | 'extras'

const PricingTab: React.FC<PricingTabProps> = ({ formik, intl, selectedLanguage }) => {
  const [activeTab, setActiveTab] = useState<PricingSubTab>('general')
  const tourId = formik.values.id?.toString() || ''

  // Check if general pricing exists
  const { data: pricingData, isLoading: isPricingLoading } = useQuery(
    ['tourPricing', tourId],
    () => getAll(null, `/tour-prices/by-tour/${tourId}`),
    {
      enabled: !!tourId,
    }
  )

  const hasGeneralPricing = !!pricingData?.data?._id

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralPricing tourId={tourId} intl={intl} />
      case 'seasonal':
        return tourId ? <SeasonalPricing tourId={tourId} intl={intl} /> : null
      case 'discounts':
        return tourId ? <Discounts tourId={tourId} intl={intl} /> : null
      case 'promotions':
        return tourId ? <Promotions tourId={tourId} intl={intl} /> : null
      case 'extras':
        if (!tourId) return null;
        if (!hasGeneralPricing) {
          return (
            <div className='alert alert-warning'>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-dark'>
                  {intl.formatMessage({id: 'TOUR.EXTRAS.NO_TOUR_PRICE'})}
                </h4>
                <span>{intl.formatMessage({id: 'TOUR.EXTRAS.NO_TOUR_PRICE_DESCRIPTION'})}</span>
              </div>
            </div>
          )
        }
        return <Extras tourId={tourId} intl={intl} selectedLanguage={selectedLanguage} />
      default:
        return null
    }
  }

  const renderTabButton = (tabId: PricingSubTab, tabLabel: string) => {
    const isDisabled = tabId === 'extras' && !hasGeneralPricing
    
    const button = (
      <div 
        className={`nav-item ${activeTab === tabId ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={() => !isDisabled && setActiveTab(tabId)}
      >
        <a 
          className={`nav-link py-3 px-4 cursor-pointer ${activeTab === tabId ? 'active' : ''} ${isDisabled ? 'disabled text-muted' : ''}`}
          style={{ 
            backgroundColor: activeTab === tabId ? '#e1f0ff' : '', 
            borderRadius: '8px',
            margin: '0 5px',
            borderBottom: activeTab === tabId ? '1px solid #e1f0ff' : 'none',
            opacity: isDisabled ? 0.5 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          <span className='fw-bold fs-6'>
            {intl.formatMessage({ id: tabLabel })}
          </span>
        </a>
      </div>
    )

    if (isDisabled) {
      return (
        <OverlayTrigger
          key={tabId}
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${tabId}`}>
              {intl.formatMessage({id: 'TOUR.EXTRAS.NO_TOUR_PRICE_DESCRIPTION'})}
            </Tooltip>
          }
        >
          {button}
        </OverlayTrigger>
      )
    }

    return button
  }

  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({ id: 'TOUR.PRICING.TITLE' })}</h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          {/* Horizontal tabs */}
          <div className='d-flex justify-content-center mb-10'>
            <div className='nav nav-tabs nav-line-tabs mb-5 fs-6 w-100' style={{ borderBottom: 'none' }}>
              {renderTabButton('general', 'TOUR.PRICING.GENERAL_PRICING')}
              {renderTabButton('seasonal', 'TOUR.PRICING.SEASONAL_PRICING')}
              {renderTabButton('discounts', 'TOUR.PRICING.DISCOUNTS')}
              {renderTabButton('promotions', 'TOUR.PRICING.PROMOTIONS')}
              {renderTabButton('extras', 'TOUR.PRICING.EXTRAS')}
            </div>
          </div>

          {/* Tab content */}
          <div className='flex-row-fluid'>
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

export { PricingTab } 